import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { scrollToTarget, hashTarget } from "@/lib/scrollToTarget";

// Site-wide scroll manager. Replaces the previous naive "scroll to top on
// every pathname change" implementation, which was overwriting native
// browser scroll restoration and dropping customers back at the top of
// the Home page after they hit Back from a product / service / workstation
// detail page.
//
// Behaviour:
//   • PUSH  / REPLACE (a new page navigation)  → scroll to top.
//   • POP   (browser Back or Forward)          → restore the saved
//                                                scroll position for
//                                                that history entry.
//
// Storage: an in-memory Map keyed by React Router's `location.key`.
// Every history entry gets a unique key so restoration is unambiguous
// even for the same URL visited twice. We save scroll positions LIVE
// as the customer scrolls, rather than on effect cleanup — cleanup
// fires after the destination route has already committed to the DOM,
// which means the browser may have already clamped scrollY to a
// shorter destination page's max before we can capture the "real"
// value. Live tracking sidesteps that entirely.
//
// Async pages (Home shows a "Loading…" skeleton until every /api/...
// call resolves) grow in height AFTER first paint, so restoration also
// uses a ResizeObserver on documentElement and re-applies the target
// each time the layout grows. Restoration stops as soon as we reach
// the target or a 5 s budget elapses.
//
// We also flip `window.history.scrollRestoration` to "manual" so the
// browser's own restoration can't fight us.
const scrollPositions = new Map();

export const ScrollToTop = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const currentKeyRef = useRef(location.key);
  const restoringRef = useRef(false);
  // `savingRef` gates the live scroll listener. Flipped to false while a
  // click / keyboard-activation is in flight so the browser's own
  // click-time scrollIntoView() / focus() can't clobber the pristine
  // scrollY we captured on pointerdown/keydown. Restored to true when
  // location.key changes (navigation landed) or after a 500 ms
  // safety timeout (click didn't lead to a navigation).
  const savingRef = useRef(true);

  // One-time: opt out of the browser's automatic scroll restoration
  // and install THREE capture mechanisms so scrollPositions always
  // holds the customer's LAST intentional scroll for the location
  // they are leaving:
  //   (1) A live scroll listener writes scrollY under the current
  //       location key as the customer scrolls.
  //   (2) A capture-phase pointerdown listener snapshots scrollY the
  //       instant a click begins — BEFORE any element.scrollIntoView()
  //       or focus() call triggered by the click can shift the
  //       viewport. This is the authoritative snapshot: after
  //       pointerdown we FREEZE all subsequent scroll writes for the
  //       rest of the click cycle so we never overwrite the good
  //       value with a value produced by the browser's own click-
  //       time scroll adjustments (e.g. clicking an off-screen link).
  //   (3) A capture-phase keydown listener does the same for
  //       keyboard-driven navigation (Enter / Space on a focused
  //       link).
  //
  // Freeze auto-releases 500 ms later so we don't get stuck if the
  // click didn't lead to a navigation.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    let freezeTimer = 0;
    const freezeSaving = () => {
      scrollPositions.set(currentKeyRef.current, window.scrollY);
      savingRef.current = false;
      window.clearTimeout(freezeTimer);
      freezeTimer = window.setTimeout(() => {
        savingRef.current = true;
      }, 500);
    };
    const onPointerDown = () => { freezeSaving(); };
    const onKeyDown = (e) => {
      // Only freeze on activation keys — regular typing shouldn't
      // pause scroll tracking on a long article, for instance.
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        freezeSaving();
      }
    };
    const onScroll = () => {
      // While a POP restoration is actively driving the scroll, ignore
      // scroll events so we don't overwrite the saved target with an
      // intermediate value produced by our own scrollTo() calls.
      if (restoringRef.current) return;
      // While a click / keyboard-activation is in flight, ignore
      // scroll events so click-time scrollIntoView() / focus() can't
      // clobber the pristine value we captured on pointerdown.
      if (!savingRef.current) return;
      scrollPositions.set(currentKeyRef.current, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { capture: true, passive: true });
    window.addEventListener("keydown", onKeyDown, { capture: true, passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointerdown", onPointerDown, { capture: true });
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.clearTimeout(freezeTimer);
    };
  }, []);

  // Keep the ref in sync with the current location key so the scroll
  // listener always writes under the right key. Runs synchronously
  // with commit so the ref is up-to-date before any scroll events fire.
  // Also unfreezes scroll tracking now that the navigation has landed.
  useEffect(() => {
    currentKeyRef.current = location.key;
    savingRef.current = true;
  }, [location.key]);

  // Apply the correct scroll for the location the customer is ARRIVING
  // at. Pages that load data asynchronously grow in height after first
  // paint — we drive restoration off a ResizeObserver so every layout
  // grow re-tries, stopping as soon as we reach the target or a 5 s
  // budget elapses.
  // Last committed location — lets an in-page REPLACE (e.g. the /services
  // category chips swapping ?group=) keep the visitor's position.
  const prevLocRef = useRef(null);

  useEffect(() => {
    const prev = prevLocRef.current;
    prevLocRef.current = location;
    if (navType !== "POP") {
      if (navType === "REPLACE" && prev && prev !== location && prev.pathname === location.pathname) {
        return undefined;
      }
      // Fresh navigation: start at the top, or — when the URL carries a
      // #hash — land once on that section after it renders.
      scrollPositions.delete(location.key);
      restoringRef.current = true;
      window.scrollTo(0, 0);
      const stopHash = location.hash ? scrollToTarget(hashTarget(location.hash)) : null;
      const t = window.setTimeout(() => { restoringRef.current = false; }, 50);
      return () => {
        window.clearTimeout(t);
        if (stopHash) stopHash();
      };
    }
    const saved = scrollPositions.get(location.key);
    // Direct load / refresh of a #hash URL (no saved offset yet).
    if (typeof saved !== "number" && location.hash) {
      window.scrollTo(0, 0);
      return scrollToTarget(hashTarget(location.hash));
    }
    const target = typeof saved === "number" ? saved : 0;
    let cancelled = false;
    const start = performance.now();
    const budgetMs = 5000;
    restoringRef.current = true;
    let observer = null;
    const release = () => {
      cancelled = true;
      if (observer) observer.disconnect();
      restoringRef.current = false;
    };
    // The visitor scrolling during restoration wins — never yank them back.
    const intents = ["wheel", "touchstart", "keydown"];
    intents.forEach((ev) => window.addEventListener(ev, release, { passive: true }));
    const attempt = () => {
      if (cancelled) return true;
      window.scrollTo(0, target);
      const reached = Math.abs(window.scrollY - target) < 2;
      const timedOut = performance.now() - start > budgetMs;
      return reached || timedOut;
    };
    // Keep re-pinning on every layout growth until the page has been quiet
    // for a moment — async sections above the target (hero media, brand
    // strips) otherwise shift the restored view via scroll anchoring.
    let quiet = 0;
    const settle = () => {
      if (attempt()) {
        window.clearTimeout(quiet);
        quiet = window.setTimeout(release, 700);
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(settle));
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(settle);
      observer.observe(document.documentElement);
    }
    const safety = window.setTimeout(release, budgetMs + 200);
    return () => {
      release();
      intents.forEach((ev) => window.removeEventListener(ev, release));
      window.clearTimeout(safety);
      window.clearTimeout(quiet);
    };
  }, [location.key, navType]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
